import React, { useState, useEffect, useCallback } from "react";
import { supabase } from "../config/supabase";
import { User } from "@supabase/supabase-js";
import { getGoalForDate, getLocalDateString } from "../utils/goalUtils";

interface PatientStatisticsViewProps {
  user: User | null;
  darkMode: boolean;
}

interface Patient {
  user_id: string;
  name: string;
  surname: string;
  email: string;
  treatment_type?: string;
}

interface ChildSummary {
  id: string;
  name: string;
}

interface ChildWeeklyRow {
  id: string;
  name: string;
  weeklyTotal: number;
  weeklyGoal: number;
}

interface EnhancedAnalytics {
  firstEntryDate: string | null;
  totalDaysWithEntries: number;
  totalDaysWithGoals: number;
  totalDays: number;
  weeklyPercentages: Array<{
    week: string;
    totalMinutes: number;
    goalMinutes: number;
    percentage: number;
  }>;
  consistencyData: Array<{
    month: string;
    daysActive: number;
    totalDays: number;
    consistencyRate: number;
    daysWithGoalsMet: number;
    dailyGoalRate: number;
  }>;
}

// Cache for patient data to avoid refetching
const patientsCache = new Map<string, { data: Patient[]; timestamp: number }>();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

const formatTime = (minutes: number) => {
  const hours = Math.floor(minutes / 60);
  const mins = Math.round((minutes % 60) * 10) / 10; // Round to 1 decimal
  return `${hours}h ${mins}m`;
};

const formatPercentage = (value: number) => {
  return `${value.toFixed(1)}%`;
};

const PatientStatisticsView: React.FC<PatientStatisticsViewProps> = ({
  user,
  darkMode,
}) => {
  const [patients, setPatients] = useState<Patient[]>([]);
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState<EnhancedAnalytics | null>(null);
  const [children, setChildren] = useState<ChildSummary[]>([]);
  const [selectedChild, setSelectedChild] = useState<ChildSummary | null>(null);
  const [childWeekly, setChildWeekly] = useState<ChildWeeklyRow[] | null>(null);
  const ringColors = [
    "#ef4444",
    "#22c55e",
    "#3b82f6",
    "#f59e0b",
    "#a855f7",
    "#10b981",
    "#e11d48",
    "#06b6d4",
  ];

  // Optimized fetch function with caching and single query
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchPatients = useCallback(async () => {
    if (!user) return;

    // Check cache first
    const cacheKey = user.id;
    const cached = patientsCache.get(cacheKey);
    const now = Date.now();

    if (cached && now - cached.timestamp < CACHE_DURATION) {
      setPatients(cached.data);
      if (cached.data.length > 0 && !selectedPatient) {
        setSelectedPatient(cached.data[0]);
      }
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      // Get patient IDs for this doctor
      const { data: accessData, error: accessError } = await supabase
        .from("doctor_user_access")
        .select("patient_id")
        .eq("doctor_id", user.id);

      if (accessError) throw accessError;

      if (!accessData || accessData.length === 0) {
        setPatients([]);
        // Cache empty result
        patientsCache.set(cacheKey, {
          data: [],
          timestamp: now,
        });
        return;
      }

      const patientIds = accessData.map((access) => access.patient_id);

      // Fetch user data for these patients
      const { data: patientsData, error } = await supabase
        .from("user_data")
        .select(
          `
          user_id,
          name,
          surname,
          email,
          treatment_type
        `,
        )
        .in("user_id", patientIds);

      if (error) throw error;

      const transformedPatients: Patient[] =
        patientsData?.map((patient: any) => ({
          user_id: patient.user_id,
          name: patient.name || "",
          surname: patient.surname || "",
          email: patient.email || "",
          treatment_type: patient.treatment_type || undefined,
        })) || [];

      // Cache the results
      patientsCache.set(cacheKey, {
        data: transformedPatients,
        timestamp: now,
      });

      setPatients(transformedPatients);

      // Auto-select first patient if none selected
      if (transformedPatients.length > 0 && !selectedPatient) {
        setSelectedPatient(transformedPatients[0]);
      }
    } catch (error) {
      console.error("Error fetching patients:", error);
      setPatients([]);
    } finally {
      setLoading(false);
    }
  }, [user]); // selectedPatient intentionally excluded to prevent re-fetch loops

  // Fetch enhanced analytics for selected patient
  const fetchEnhancedAnalytics = useCallback(async () => {
    if (!selectedPatient) return;

    try {
      let weeklyGoal = 1680;
      let logs: any[] = [];
      let entityCreatedAt: string | null = null;
      let goalsRow: any = null;

      if (selectedPatient.treatment_type === "child" && selectedChild) {
        // Child path: get child goals and child logs
        const { data: cg, error: cgErr } = await supabase
          .from("child_goals")
          .select(
            "weekly_goal_minutes, monday_goal_minutes, tuesday_goal_minutes, wednesday_goal_minutes, thursday_goal_minutes, friday_goal_minutes, saturday_goal_minutes, sunday_goal_minutes",
          )
          .eq("child_id", selectedChild.id)
          .single();
        if (cgErr && cgErr.code !== "PGRST116") {
          console.warn("Error fetching child goals:", cgErr);
        }
        goalsRow = cg || null;
        weeklyGoal = cg?.weekly_goal_minutes || 1680;

        // Get child creation date for monthly total-days baseline
        try {
          const { data: childMeta } = await supabase
            .from("children")
            .select("created_at")
            .eq("id", selectedChild.id)
            .single();
          entityCreatedAt = childMeta?.created_at ?? null;
        } catch {}

        const { data: childLogs, error: clErr } = await supabase
          .from("child_logs")
          .select("date, time_spent_minutes, daily_goal_at_time")
          .eq("child_id", selectedChild.id)
          .order("date", { ascending: true });
        if (clErr) throw clErr;
        logs = childLogs || [];
      } else {
        // Adult/self path: get user goals and user logs
        const { data: goalsData, error: goalsError } = await supabase
          .from("user_goals")
          .select(
            "weekly_goal_minutes, monday_goal_minutes, tuesday_goal_minutes, wednesday_goal_minutes, thursday_goal_minutes, friday_goal_minutes, saturday_goal_minutes, sunday_goal_minutes",
          )
          .eq("user_id", selectedPatient.user_id)
          .single();

        if (goalsError && goalsError.code !== "PGRST116") {
          console.error("Error fetching user goals:", goalsError);
        }

        goalsRow = goalsData || null;
        weeklyGoal = goalsData?.weekly_goal_minutes || 1680;

        // Get user creation date from user_data
        try {
          const { data: userMeta } = await supabase
            .from("user_data")
            .select("created_at")
            .eq("user_id", selectedPatient.user_id)
            .single();
          entityCreatedAt = userMeta?.created_at ?? null;
        } catch {}

        const { data: logsData, error: logsError } = await supabase
          .from("daily_logs")
          .select("date, time_spent_minutes, daily_goal_at_time")
          .eq("user_id", selectedPatient.user_id)
          .order("date", { ascending: true });
        if (logsError) throw logsError;
        logs = logsData || [];
      }
      // Calculate analytics
      const firstEntryDate = logs.length > 0 ? logs[0].date : null;

      // Monthly baseline: start of this month, but if the entity was created later OR
      // the first entry this month is later, start from the later date
      const now = new Date();
      const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
      const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      const monthEndStr = getLocalDateString(monthEnd);

      const firstEntryDateObj = firstEntryDate
        ? new Date(firstEntryDate)
        : null;
      const firstEntryThisMonth =
        firstEntryDateObj && firstEntryDateObj >= monthStart
          ? firstEntryDateObj
          : null;
      const createdAtDate = entityCreatedAt ? new Date(entityCreatedAt) : null;

      let baselineDate = monthStart;
      if (createdAtDate && createdAtDate > baselineDate)
        baselineDate = createdAtDate;
      if (firstEntryThisMonth && firstEntryThisMonth > baselineDate)
        baselineDate = firstEntryThisMonth;
      const baselineStr = getLocalDateString(baselineDate);

      // Days with entries this month from baseline
      const uniqueDates = new Set(
        logs
          .filter((l) => l.date >= baselineStr && l.date <= monthEndStr)
          .map((l) => l.date),
      );
      const totalDaysWithEntries = uniqueDates.size;

      // Days with goals met: count of unique days where SUM of all logs >= daily goal
      const goalMetDates = new Set();
      const logsByDate = logs.reduce(
        (acc, log) => {
          if (!acc[log.date]) {
            acc[log.date] = [];
          }
          acc[log.date].push({
            minutes: log.time_spent_minutes,
            daily_goal_at_time: (log as any).daily_goal_at_time,
          });
          return acc;
        },
        {} as Record<
          string,
          Array<{ minutes: number; daily_goal_at_time?: number | null }>
        >,
      );

      // Check each date: if SUM of all logs meets the goal, count that date
      Object.entries(logsByDate).forEach(([date, entries]) => {
        const totalMinutesForDay = (entries as any[]).reduce(
          (sum, e) => sum + (e.minutes || 0),
          0,
        );
        const dayEntries = entries as Array<{
          minutes: number;
          daily_goal_at_time?: number | null;
        }>;
        const hasRestDayLog = dayEntries.some(
          (e) => e.daily_goal_at_time === null,
        );
        const historicalGoal = dayEntries.find(
          (e) => e.daily_goal_at_time !== undefined,
        )?.daily_goal_at_time;
        const dayGoal = hasRestDayLog
          ? null
          : historicalGoal !== undefined
            ? historicalGoal
            : ((goalsRow ? getGoalForDate(goalsRow, date) : null) ?? 240);

        if (dayGoal === null || totalMinutesForDay >= dayGoal) {
          goalMetDates.add(date);
        }
      });

      let totalDaysWithGoals = goalMetDates.size;

      // Total days: days in current month from baseline to today (inclusive)
      const totalDays =
        Math.floor(
          (now.getTime() - baselineDate.getTime()) / (1000 * 60 * 60 * 24),
        ) + 1;

      // Days with goals met: restrict to current month from baseline
      totalDaysWithGoals = Array.from(goalMetDates).filter((d) => {
        const ds = String(d);
        return ds >= baselineStr && ds <= monthEndStr;
      }).length;

      // Calculate weekly percentages
      const weeklyPercentages: Array<{
        week: string;
        totalMinutes: number;
        goalMinutes: number;
        percentage: number;
      }> = [];
      const weeks = new Map<string, { totalMinutes: number; days: number }>();

      logs.forEach((log) => {
        const date = new Date(log.date);
        const weekStart = new Date(date);
        weekStart.setDate(date.getDate() - date.getDay());
        const weekKey = getLocalDateString(weekStart);

        if (!weeks.has(weekKey)) {
          weeks.set(weekKey, { totalMinutes: 0, days: 0 });
        }
        const week = weeks.get(weekKey)!;
        week.totalMinutes += log.time_spent_minutes;
        week.days += 1;
      });

      weeks.forEach((week, weekKey) => {
        weeklyPercentages.push({
          week: weekKey,
          totalMinutes: week.totalMinutes,
          goalMinutes: weeklyGoal,
          percentage: (week.totalMinutes / weeklyGoal) * 100,
        });
      });

      // Calculate consistency data with proper month calculations
      const consistencyData: Array<{
        month: string;
        daysActive: number;
        totalDays: number;
        consistencyRate: number;
        daysWithGoalsMet: number;
        dailyGoalRate: number;
      }> = [];
      const months = new Map<
        string,
        {
          daysActive: Set<string>;
          daysWithGoalsMet: Set<string>;
          firstLogDate: Date | null;
        }
      >();

      if (logs.length > 0) {
        // Group logs by month and track unique days
        logs.forEach((log) => {
          const date = new Date(log.date);
          const monthKey = `${date.getFullYear()}-${String(
            date.getMonth() + 1,
          ).padStart(2, "0")}`;

          if (!months.has(monthKey)) {
            months.set(monthKey, {
              daysActive: new Set(),
              daysWithGoalsMet: new Set(),
              firstLogDate: date,
            });
          }
          const month = months.get(monthKey)!;
          // Track earliest log date within this month
          if (!month.firstLogDate || date < month.firstLogDate) {
            month.firstLogDate = date;
          }
          month.daysActive.add(log.date); // Add unique date to the set
        });

        // Now calculate which days met the goal (sum of all logs for each day)
        Object.entries(logsByDate).forEach(([date, entries]) => {
          const dayEntries = entries as Array<{
            minutes: number;
            daily_goal_at_time?: number | null;
          }>;
          const totalMinutesForDay = dayEntries.reduce(
            (sum, e) => sum + (e.minutes || 0),
            0,
          );

          const hasRestDayLog = dayEntries.some(
            (e) => e.daily_goal_at_time === null,
          );
          const historicalGoal = dayEntries.find(
            (e) => e.daily_goal_at_time !== undefined,
          )?.daily_goal_at_time;
          const dayGoal = hasRestDayLog
            ? null
            : historicalGoal !== undefined
              ? historicalGoal
              : ((goalsRow ? getGoalForDate(goalsRow, date) : null) ?? 240);

          if (dayGoal === null || totalMinutesForDay >= dayGoal) {
            // Find which month this date belongs to
            const dateObj = new Date(date);
            const monthKey = `${dateObj.getFullYear()}-${String(
              dateObj.getMonth() + 1,
            ).padStart(2, "0")}`;

            const month = months.get(monthKey);
            if (month) {
              month.daysWithGoalsMet.add(date);
            }
          }
        });
      }

      months.forEach((month, monthKey) => {
        const daysWithGoalsMet = month.daysWithGoalsMet.size;

        // Compute total days for the month starting from the first log date in that month
        const [y, m] = monthKey.split("-").map((v) => parseInt(v, 10));
        const monthIndex = (m || 1) - 1; // 0-based
        const monthEnd = new Date(y || 1970, monthIndex + 1, 0);
        const isCurrentMonth =
          y === now.getFullYear() && monthIndex === now.getMonth();

        // If no firstLogDate (shouldn't happen), default to month start
        const fromDate =
          month.firstLogDate || new Date(y || 1970, monthIndex, 1);
        const toDate = isCurrentMonth ? now : monthEnd;

        const msPerDay = 1000 * 60 * 60 * 24;
        const totalDaysForMonth = Math.max(
          1,
          Math.floor((toDate.getTime() - fromDate.getTime()) / msPerDay) + 1,
        );

        const consistencyRate =
          totalDaysForMonth > 0
            ? (month.daysActive.size / totalDaysForMonth) * 100
            : 0;
        const dailyGoalRate =
          totalDaysForMonth > 0
            ? (daysWithGoalsMet / totalDaysForMonth) * 100
            : 0;

        consistencyData.push({
          month: monthKey,
          daysActive: month.daysActive.size, // Count unique days
          totalDays: totalDaysForMonth,
          consistencyRate: consistencyRate,
          daysWithGoalsMet: daysWithGoalsMet,
          dailyGoalRate: dailyGoalRate,
        });
      });

      setAnalytics({
        firstEntryDate,
        totalDaysWithEntries,
        totalDaysWithGoals,
        totalDays,
        weeklyPercentages,
        consistencyData,
      });
    } catch (error) {
      console.error("Error fetching enhanced analytics:", error);
    } finally {
      // setAnalyticsLoading(false); // This line was removed as per the edit hint
    }
  }, [selectedPatient, selectedChild]);

  // Fetch children list for child-treatment patients
  const fetchChildren = useCallback(async (patientId: string) => {
    try {
      const { data, error } = await supabase
        .from("children")
        .select("id, name")
        .eq("user_id", patientId);
      if (error) throw error;
      const list: ChildSummary[] = (data || []).map((c: any) => ({
        id: c.id,
        name: c.name || "Child",
      }));
      setChildren(list);
      // Default to all children (no specific child selected)
      setSelectedChild(null);
    } catch (err) {
      console.error("Error fetching children:", err);
      setChildren([]);
      setSelectedChild(null);
    }
  }, []);

  useEffect(() => {
    if (user) {
      fetchPatients();
    }
  }, [user, fetchPatients]);

  useEffect(() => {
    if (selectedPatient) {
      fetchEnhancedAnalytics();
    }
  }, [selectedPatient, fetchEnhancedAnalytics]);

  // When viewing child-treatment patient with All children selected, compute weekly totals list
  useEffect(() => {
    const run = async () => {
      if (
        selectedPatient?.treatment_type === "child" &&
        !selectedChild &&
        children.length > 0
      ) {
        const childIds = children.map((c) => c.id);

        // Get current week (Sunday to Saturday)
        const today = new Date();

        const startOfWeek = new Date(today);
        startOfWeek.setDate(today.getDate() - today.getDay());
        startOfWeek.setHours(0, 0, 0, 0);

        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(startOfWeek.getDate() + 6);
        endOfWeek.setHours(23, 59, 59, 999);

        const [
          { data: logs, error: logsErr },
          { data: goals, error: goalsErr },
        ] = await Promise.all([
          supabase
            .from("child_logs")
            .select("child_id, time_spent_minutes, date")
            .in("child_id", childIds)
            .gte("date", getLocalDateString(startOfWeek))
            .lte("date", getLocalDateString(endOfWeek)),
          supabase
            .from("child_goals")
            .select("child_id, weekly_goal_minutes")
            .in("child_id", childIds),
        ] as any);

        if (logsErr) {
          console.error("Error fetching child weekly logs:", logsErr);
          setChildWeekly([]);
          return;
        }
        if (goalsErr) {
          console.warn("Error fetching child goals:", goalsErr);
        }

        const goalsMap = new Map<string, number>();
        (goals || []).forEach((g: any) =>
          goalsMap.set(g.child_id, g.weekly_goal_minutes || 1680),
        );

        const totalsMap = new Map<string, number>();
        (logs || []).forEach((row: any) => {
          const prev = totalsMap.get(row.child_id) || 0;
          totalsMap.set(row.child_id, prev + (row.time_spent_minutes || 0));
        });

        const rows: ChildWeeklyRow[] = children.map((c) => {
          const weeklyTotal = totalsMap.get(c.id) || 0;
          const weeklyGoal = goalsMap.get(c.id) || 1680;

          return {
            id: c.id,
            name: c.name,
            weeklyTotal: weeklyTotal,
            weeklyGoal: weeklyGoal,
          };
        });

        setChildWeekly(rows);
      } else {
        setChildWeekly(null);
      }
    };
    run();
  }, [selectedPatient, selectedChild, children]);

  useEffect(() => {
    if (selectedPatient?.treatment_type === "child") {
      fetchChildren(selectedPatient.user_id);
    } else {
      setChildren([]);
      setSelectedChild(null);
    }
  }, [selectedPatient, fetchChildren]);

  // Optimized patient selection handler
  /* eslint-disable react-hooks/exhaustive-deps */
  const handlePatientChange = useCallback(
    (patientId: string) => {
      const patient = patients.find((p) => p.user_id === patientId);
      setSelectedPatient(patient || null);
    },
    [patients], // selectedPatient intentionally excluded to avoid infinite loops
  );
  /* eslint-enable react-hooks/exhaustive-deps */

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">Loading patients...</div>
      </div>
    );
  }

  if (patients.length === 0) {
    return (
      <div className="text-center py-12">
        <h2
          className={`text-2xl font-bold mb-4 ${
            darkMode ? "text-white" : "text-brand-dark-blue"
          }`}
        >
          Patient Statistics View
        </h2>
        <p
          className={`text-lg ${darkMode ? "text-gray-300" : "text-gray-600"}`}
        >
          No patients assigned to you yet.
        </p>
        <p
          className={`text-sm mt-2 ${
            darkMode ? "text-gray-400" : "text-gray-500"
          }`}
        >
          Visit the Patients tab to add patients to your care.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Patient Selector */}
      <div className="text-center px-4">
        <h2
          className={`text-2xl sm:text-3xl font-bold mb-4 ${
            darkMode ? "text-white" : "text-brand-dark-blue"
          }`}
        >
          Patient Statistics View
        </h2>
        <div className="flex justify-center">
          <div className="relative w-full max-w-md">
            <select
              value={selectedPatient?.user_id || ""}
              onChange={(e) => handlePatientChange(e.target.value)}
              className={`w-full px-4 py-3 text-sm sm:text-base rounded-lg border-2 focus:ring-2 focus:ring-brand-cyan focus:border-transparent transition-colors ${
                darkMode
                  ? "bg-gray-700 border-gray-600 text-white"
                  : "bg-white border-gray-300 text-gray-900"
              }`}
            >
              <option value="">Select a patient...</option>
              {patients.map((patient) => (
                <option key={patient.user_id} value={patient.user_id}>
                  {patient.name && patient.surname
                    ? `${patient.name} ${patient.surname} (${patient.email})`
                    : patient.email}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {selectedPatient?.treatment_type === "child" && (
        <div className="flex justify-center px-4">
          <div className="relative w-full max-w-md">
            <select
              value={selectedChild?.id || ""}
              onChange={(e) =>
                setSelectedChild(
                  children.find((c) => c.id === e.target.value) || null,
                )
              }
              className={`w-full mt-2 px-4 py-3 text-sm sm:text-base rounded-lg border-2 focus:ring-2 focus:ring-brand-cyan focus:border-transparent transition-colors ${
                darkMode
                  ? "bg-gray-700 border-gray-600 text-white"
                  : "bg-white border-gray-300 text-gray-900"
              }`}
            >
              <option value="">All children</option>
              {children.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* All children rings + legend */}
      {selectedPatient?.treatment_type === "child" &&
        !selectedChild &&
        childWeekly && (
          <div
            className={`p-4 sm:p-6 rounded-lg mx-4 sm:mx-0 ${
              darkMode ? "bg-gray-800" : "bg-white"
            } ${
              darkMode
                ? "shadow-[0_12px_50px_rgba(0,0,0,0.6)] ring-1 ring-black/40"
                : "shadow-lg"
            }`}
          >
            <h4
              className={`text-lg font-semibold mb-4 text-center ${
                darkMode ? "text-white" : "text-gray-900"
              }`}
            >
              All Children (This Week)
            </h4>
            <div className="flex flex-col items-center">
              <div className="mb-4">
                <svg
                  width={200}
                  height={200}
                  className="transform -rotate-90 sm:w-[240px] sm:h-[240px]"
                >
                  {childWeekly.map((row, idx) => {
                    const radiusBase = 110; // half of 240 minus stroke
                    const strokeWidth = 10;
                    const ringSpacing = 8;
                    const radius =
                      radiusBase - idx * (strokeWidth + ringSpacing);
                    if (radius <= 0) return null;
                    const circumference = radius * 2 * Math.PI;
                    const pct = Math.min(
                      (row.weeklyTotal / Math.max(row.weeklyGoal || 1, 1)) *
                        100,
                      100,
                    );
                    const dashoffset =
                      circumference - (pct / 100) * circumference;
                    return (
                      <g key={row.id}>
                        <circle
                          cx={120}
                          cy={120}
                          r={radius}
                          stroke={darkMode ? "#374151" : "#e5e7eb"}
                          strokeWidth={strokeWidth}
                          fill="transparent"
                        />
                        <circle
                          cx={120}
                          cy={120}
                          r={radius}
                          stroke={ringColors[idx % ringColors.length]}
                          strokeWidth={strokeWidth}
                          fill="transparent"
                          strokeDasharray={`${circumference} ${circumference}`}
                          strokeDashoffset={dashoffset}
                          strokeLinecap="round"
                        />
                      </g>
                    );
                  })}
                </svg>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 sm:gap-x-8 gap-y-2 w-full max-w-md px-2">
                {childWeekly.map((row, idx) => (
                  <div
                    key={row.id}
                    className="flex items-center justify-between"
                  >
                    <div className="flex items-center space-x-2">
                      <span
                        className="inline-block w-3 h-3 rounded-full"
                        style={{
                          backgroundColor: ringColors[idx % ringColors.length],
                        }}
                      />
                      <span
                        className={`${
                          darkMode ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        {row.name}
                      </span>
                    </div>
                    <span
                      className={`${
                        darkMode ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      {formatTime(row.weeklyTotal)} /{" "}
                      {formatTime(row.weeklyGoal)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

      {/* Enhanced Analytics (single child or adult) */}
      {selectedPatient &&
        analytics &&
        (selectedPatient.treatment_type !== "child" || !!selectedChild) && (
          <div className="space-y-6 px-4 sm:px-0">
            <div className="text-center mb-4">
              <h3
                className={`text-lg sm:text-xl font-semibold ${
                  darkMode ? "text-white" : "text-gray-900"
                }`}
              >
                {selectedPatient.treatment_type === "child" && selectedChild
                  ? `Analytics for ${
                      children.find((c) => c.id === selectedChild.id)?.name ||
                      "Child"
                    }`
                  : `Analytics for ${
                      selectedPatient.name && selectedPatient.surname
                        ? `${selectedPatient.name} ${selectedPatient.surname}`
                        : selectedPatient.email
                    }`}
              </h3>
            </div>

            {/* First Row - Key Metrics */}
            <div
              className={`p-4 sm:p-6 rounded-lg ${
                darkMode ? "bg-gray-800" : "bg-white"
              } ${
                darkMode
                  ? "shadow-[0_12px_50px_rgba(0,0,0,0.6)] ring-1 ring-black/40"
                  : "shadow-lg"
              }`}
            >
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                <div className="text-center">
                  <div
                    className={`text-2xl font-bold ${
                      darkMode ? "text-blue-400" : "text-blue-600"
                    }`}
                  >
                    {analytics.firstEntryDate
                      ? new Date(analytics.firstEntryDate).toLocaleDateString()
                      : "N/A"}
                  </div>
                  <div
                    className={`text-sm ${
                      darkMode ? "text-gray-400" : "text-gray-600"
                    }`}
                  >
                    First Entry Date
                  </div>
                </div>
                <div className="text-center">
                  <div
                    className={`text-2xl font-bold ${
                      darkMode ? "text-green-400" : "text-green-600"
                    }`}
                  >
                    {analytics.totalDaysWithEntries}
                  </div>
                  <div
                    className={`text-sm ${
                      darkMode ? "text-gray-400" : "text-gray-600"
                    }`}
                  >
                    Days with Entries
                  </div>
                </div>
                <div className="text-center">
                  <div
                    className={`text-2xl font-bold ${
                      darkMode ? "text-purple-400" : "text-purple-600"
                    }`}
                  >
                    {analytics.totalDaysWithGoals}
                  </div>
                  <div
                    className={`text-sm ${
                      darkMode ? "text-gray-400" : "text-gray-600"
                    }`}
                  >
                    Days with Goals Met
                  </div>
                </div>
                <div className="text-center">
                  <div
                    className={`text-2xl font-bold ${
                      darkMode ? "text-orange-400" : "text-orange-600"
                    }`}
                  >
                    {analytics.totalDays}
                  </div>
                  <div
                    className={`text-sm ${
                      darkMode ? "text-gray-400" : "text-gray-600"
                    }`}
                  >
                    Total Days
                  </div>
                </div>
              </div>
            </div>

            {/* Weekly Percentages */}
            <div
              className={`p-4 sm:p-6 rounded-lg ${
                darkMode ? "bg-gray-800" : "bg-white"
              } ${
                darkMode
                  ? "shadow-[0_12px_50px_rgba(0,0,0,0.6)] ring-1 ring-black/40"
                  : "shadow-lg"
              }`}
            >
              <h4
                className={`text-lg font-semibold mb-4 ${
                  darkMode ? "text-white" : "text-gray-900"
                }`}
              >
                Weekly Performance
              </h4>
              <div className="space-y-3">
                {analytics.weeklyPercentages.map((week) => (
                  <div
                    key={week.week}
                    className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2"
                  >
                    <span
                      className={`text-sm sm:text-base ${
                        darkMode ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Week of {new Date(week.week).toLocaleDateString()}
                    </span>
                    <div className="flex items-center justify-between sm:justify-end sm:space-x-4">
                      <span
                        className={`text-xs sm:text-sm ${
                          darkMode ? "text-gray-400" : "text-gray-600"
                        }`}
                      >
                        {formatTime(week.totalMinutes)} /{" "}
                        {formatTime(week.goalMinutes)}
                      </span>
                      <span
                        className={`font-semibold ${
                          week.percentage >= 100
                            ? "text-green-500"
                            : "text-blue-500"
                        }`}
                      >
                        {formatPercentage(week.percentage)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Consistency Table */}
            <div
              className={`p-4 sm:p-6 rounded-lg ${
                darkMode ? "bg-gray-800" : "bg-white"
              } ${
                darkMode
                  ? "shadow-[0_12px_50px_rgba(0,0,0,0.6)] ring-1 ring-black/40"
                  : "shadow-lg"
              }`}
            >
              <h4
                className={`text-lg font-semibold mb-4 ${
                  darkMode ? "text-white" : "text-gray-900"
                }`}
              >
                Monthly Consistency
              </h4>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[600px]">
                  <thead>
                    <tr
                      className={`border-b ${
                        darkMode ? "border-gray-700" : "border-gray-300"
                      }`}
                    >
                      <th
                        className={`text-left py-2 ${
                          darkMode ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        Month
                      </th>
                      <th
                        className={`text-left py-2 ${
                          darkMode ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        Days Active
                      </th>
                      <th
                        className={`text-left py-2 ${
                          darkMode ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        Total Days
                      </th>
                      <th
                        className={`text-left py-2 ${
                          darkMode ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        Consistency Rate
                      </th>
                      <th
                        className={`text-left py-2 ${
                          darkMode ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        Daily Goal Rate
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {analytics.consistencyData.map((month) => (
                      <tr
                        key={month.month}
                        className={`border-b ${
                          darkMode ? "border-gray-700" : "border-gray-200"
                        }`}
                      >
                        <td
                          className={`py-2 ${
                            darkMode ? "text-gray-300" : "text-gray-700"
                          }`}
                        >
                          {new Date(month.month + "-01").toLocaleDateString(
                            "en-US",
                            { month: "long", year: "numeric" },
                          )}
                        </td>
                        <td
                          className={`py-2 ${
                            darkMode ? "text-gray-300" : "text-gray-700"
                          }`}
                        >
                          {month.daysActive}
                        </td>
                        <td
                          className={`py-2 ${
                            darkMode ? "text-gray-300" : "text-gray-700"
                          }`}
                        >
                          {month.totalDays}
                        </td>
                        <td
                          className={`py-2 ${
                            darkMode ? "text-gray-300" : "text-gray-700"
                          }`}
                        >
                          <span
                            className={`font-semibold ${
                              month.consistencyRate >= 80
                                ? "text-green-500"
                                : month.consistencyRate >= 60
                                  ? "text-yellow-500"
                                  : "text-red-500"
                            }`}
                          >
                            {formatPercentage(month.consistencyRate)}
                          </span>
                        </td>
                        <td
                          className={`py-2 ${
                            darkMode ? "text-gray-300" : "text-gray-700"
                          }`}
                        >
                          <span
                            className={`font-semibold ${
                              month.dailyGoalRate >= 80
                                ? "text-green-500"
                                : month.dailyGoalRate >= 60
                                  ? "text-yellow-500"
                                  : "text-red-500"
                            }`}
                          >
                            {formatPercentage(month.dailyGoalRate)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* PDF Export Placeholder */}
            <div
              className={`p-4 sm:p-6 rounded-lg ${
                darkMode ? "bg-gray-800" : "bg-white"
              } ${
                darkMode
                  ? "shadow-[0_12px_50px_rgba(0,0,0,0.6)] ring-1 ring-black/40"
                  : "shadow-lg"
              }`}
            >
              <h4
                className={`text-lg font-semibold mb-4 ${
                  darkMode ? "text-white" : "text-gray-900"
                }`}
              >
                Export Report
              </h4>
              <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:space-x-4 sm:space-y-0">
                <input
                  type="date"
                  className={`flex-1 px-3 py-2 text-sm sm:text-base border rounded-lg ${
                    darkMode
                      ? "bg-gray-700 border-gray-600 text-white"
                      : "bg-white border-gray-300"
                  }`}
                  placeholder="From date"
                />
                <input
                  type="date"
                  className={`flex-1 px-3 py-2 text-sm sm:text-base border rounded-lg ${
                    darkMode
                      ? "bg-gray-700 border-gray-600 text-white"
                      : "bg-white border-gray-300"
                  }`}
                  placeholder="To date"
                />
                <button
                  className={`px-4 py-2 text-sm sm:text-base bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors ${
                    darkMode ? "bg-blue-600 hover:bg-blue-700" : ""
                  }`}
                  onClick={() =>
                    alert("PDF export functionality will be implemented here")
                  }
                >
                  Export to PDF
                </button>
              </div>
              <p
                className={`text-xs sm:text-sm mt-2 ${
                  darkMode ? "text-gray-400" : "text-gray-600"
                }`}
              >
                PDF export functionality will be implemented in a future update.
              </p>
            </div>
          </div>
        )}
    </div>
  );
};

export default PatientStatisticsView;
